import { useQuery, gql } from '@apollo/client'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef } from 'react'
import { List } from 'immutable'
import _partial from 'lodash/partial'
import _get from 'lodash/get'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import {
  URL_ACCOUNT,
  URL_MANAGE_PROFILES,
  URL_HELP_CENTER_ES,
  URL_MARKETPLACE_LOGGED_OUT,
  URL_SHOP,
  URL_PLAYLIST_PURCHASES_VIDEOS,
  URL_PLAYLIST_BONUS_VIDEOS,
} from '../../services/url/constants'
import { GET_USER_SKU_MAPPINGS, PROVIDER_GAIA_BONUS, PROVIDER_SHOPIFY } from '../../services/playlist/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../services/apollo-graphql/index'
import { getPrimary } from '../../services/languages/index'
import { ES } from '../../services/languages/constants'
import Button, { TYPES } from '../Button/index'
import Menu from '../Menu/index'
import UserAvatar from '../UserAvatar/index'
import ToolTipArrow, { DIRECTION_UP } from '../ToolTipArrow/index'
import { usePostHogEvent } from '../PostHog/index'
import { selectCurrentUserProfilePicture } from '../../services/community/identity/profile/redux/selectors'
import { getBoundActions } from '../../actions/index'
import { get as getConfig } from '../../config/index'
import useCommunityEnabled from '../../services/community/shared/useCommunityEnabled'
import { getUserAvatarName } from '../../services/user/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import {
  isAccount,
  isAccountLanguage,
  isContact,
  isManageProfilesPage,
  isAmbassadorsPage,
} from '../../services/url/index'
import { ROLE_ID_AMBASSADOR } from '../../services/auth-access/index'
import { hasContentRelated } from '../../services/playlist/index'

const { features, marketplaceUrl } = getConfig()
const EMPTY_MENU = List()

const GET_CURRENT_DISPLAY_NAME = gql`
  query CurrentUserDisplayName {
    userProfileCurrent {
      uuid
      displayName
    }
  }
`

export function getMenuItemStructure (props) {
  const {
    user,
    menu: menuProp,
    toggleUserMenuVisible,
    auth,
    hasBonus = false,
    hasPurchases = false,
    communityProfileEnabled = false,
  } = props
  const primaryLang = getPrimary(user.getIn(['data', 'language'], List()))
  const langIsEs = primaryLang === ES
  const items = menuProp.reduce((reduction, item) => {
    // don't render the language select link if there is a feature toggle
    // @TODO: uncomment feature toggle when appropriate
    // @NOTE: I think this toggle may be dangerous to turn off and remove the entire user menu.
    if (!features.languageSelect) {
      return reduction
    }
    if (!features.languageSelectDialog && isAccountLanguage(item.get('url'))) {
      return reduction
    }

    if (isAmbassadorsPage(item.get('url'))
      && (!features.ambassadorPayouts || !auth.get('roleIds').includes(ROLE_ID_AMBASSADOR))) {
      return reduction
    }

    // Hide Bonuses if user has no bonus eligibility
    if (item.get('url') === URL_PLAYLIST_BONUS_VIDEOS && !hasBonus) {
      return reduction
    }

    // Hide Purchases if user has no purchases
    if (item.get('url') === URL_PLAYLIST_PURCHASES_VIDEOS && !hasPurchases) {
      return reduction
    }

    const menuItem = item.withMutations((mutatableItem) => {
      const isContactUrl = isContact(mutatableItem.get('url'))
      mutatableItem.set('user-menu-item', true)

      if (mutatableItem.get('border')) {
        mutatableItem.set('user-menu-border', true)
      }

      // create a link to the marketplace account area
      if (item.get('url') === URL_MARKETPLACE_LOGGED_OUT) {
        mutatableItem
          .set('url', `${URL_SHOP}?redirect=${encodeURIComponent(`${marketplaceUrl}/account`)}`)
          .set('onClick', () => toggleUserMenuVisible())
      }

      if (
        isAccount(mutatableItem.get('url'))
        || isManageProfilesPage(mutatableItem.get('url'))
        || isAmbassadorsPage(mutatableItem.get('url'))
      ) {
        mutatableItem.set('onClick', () => toggleUserMenuVisible())
      } else if (
        isContactUrl
        && langIsEs
        && _get(features, ['userLanguage', 'esZendeskHelpLink'])
      ) {
        mutatableItem.set('url', URL_HELP_CENTER_ES)
      }

      return mutatableItem
    })
    return reduction.push(menuItem)
  }, List())

  // Community-gated reorder: surface "My Account" above "Manage Profiles"
  // (Figma 3520-46123). Route-driven so i18n changes don't break the swap.
  if (!communityProfileEnabled) return items
  const accountIdx = items.findIndex((i) => i.get('url') === URL_ACCOUNT)
  const profilesIdx = items.findIndex((i) => i.get('url') === URL_MANAGE_PROFILES)
  if (accountIdx === -1 || profilesIdx === -1 || accountIdx < profilesIdx) return items
  const accountItem = items.get(accountIdx)
  return items.delete(accountIdx).insert(profilesIdx, accountItem)
}

export function renderMenu (props) {
  const {
    user,
    userProfiles,
    toggleUserMenuVisible,
    communityProfileEnabled,
    communityDisplayName,
    hasBonus,
    hasPurchases,
    trackCommunityHeaderLinkClicked,
  } = props
  const portalUrl = user.getIn(['data', 'portal', 'url'])
  const profiles = userProfiles.get('data', List())
  const className = [
    'menu--user',
    `menu--profiles-${profiles.size}`,
    communityProfileEnabled ? 'menu--community' : null,
  ].filter(Boolean).join(' ')

  return (
    <Menu
      location={global.location}
      portalUrl={portalUrl}
      className={className}
      profiles={userProfiles}
      itemClassName="menu-item--user"
      itemLinkClassName="menu-item__link--user"
      toggleUserMenuVisible={toggleUserMenuVisible}
      trackCommunityHeaderLinkClicked={trackCommunityHeaderLinkClicked}
      communityProfileEnabled={communityProfileEnabled}
      communityDisplayName={communityDisplayName}
      menu={getMenuItemStructure({
        ...props, hasBonus, hasPurchases, communityProfileEnabled,
      })}
    />
  )
}

export function onClickMenuWrapper (e) {
  e.stopPropagation()
}

export function showUserMenu (
  e,
  toggleUserMenuVisible,
  setNavigationOverlayVisible,
  setNotificationsMenuVisible,
) {
  e.stopPropagation()
  toggleUserMenuVisible()
  setNavigationOverlayVisible(false)
  setNotificationsMenuVisible(false)
}

export function renderUserAvatar (props) {
  const {
    user, currentUserPicture, communityProfileEnabled, communityDisplayName,
  } = props

  if (!user.get('data')) {
    return <div className="user-menu__user-avatar-placeholder" />
  }

  const userAvatarName = communityDisplayName || getUserAvatarName(user.getIn(['data']))
  const avatarPath = communityProfileEnabled && currentUserPicture
    ? currentUserPicture.small
    : user.getIn(['data', 'profile', 'picture', 'hdtv_190x266'], '')
  return (
    <UserAvatar
      path={avatarPath}
      className={['user-menu__avatar']}
      name={userAvatarName}
    />
  )
}

export function getClassName (hideUserMenu) {
  const className = ['user-menu__user']
  if (hideUserMenu) {
    className.push('user-menu__user--disabled')
  }
  return className.join(' ')
}

const UserMenu = (props) => {
  const {
    auth,
    menuVisible,
    hideUserMenu,
    toggleUserMenuVisible,
    setNavigationOverlayVisible,
    setNotificationsMenuVisible,
    fetchProfilePictures,
    userProfiles,
    user,
  } = props
  const userPrimaryLanguage = getPrimary(user.getIn(['data', 'language'], List()))

  const { enabled: communityProfileEnabled } = useCommunityEnabled()
  const trackCommunityHeaderLinkClicked = usePostHogEvent('community_header_link_clicked')

  // Fetch community displayName for the current user (cache-first — reuses
  // data already fetched by profile page when available).
  const { data: displayNameData } = useQuery(GET_CURRENT_DISPLAY_NAME, {
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    skip: !communityProfileEnabled || !getAuthIsLoggedIn(auth),
    context: { headers: { authorization: `Bearer ${auth.get('jwt')}` } },
  })
  const communityDisplayName = communityProfileEnabled
    ? (displayNameData?.userProfileCurrent?.displayName
      || auth.get('username')
      || null)
    : null

  // Fetch profile pictures for ALL profiles on the account so the header
  // dropdown shows Brooklyn avatars. The watcher dedupes cached UIDs.
  const uid = auth.get('uid')
  const profiles = userProfiles.get('data', List())
  useEffect(() => {
    if (!communityProfileEnabled || profiles.size === 0) return
    const uids = profiles
      .map((p) => String(p.get('uid')))
      .filter(Boolean)
      .toJS()
    if (uids.length > 0) {
      fetchProfilePictures(uids)
    }
  }, [uid, profiles.size])

  const {
    data: skuMappingsData,
    loading: mappingsLoading,
  } = useQuery(GET_USER_SKU_MAPPINGS, {
    variables: {
      language: [userPrimaryLanguage],
    },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    skip: !getAuthIsLoggedIn(auth),
    context: {
      jwt: auth.get('jwt'),
    },
  })

  const bonusMappings = _get(skuMappingsData, ['ecommerceSkuMappingsList', 'ecommerceSkuMappings'], []) || []
  const hasBonus = mappingsLoading ? false : hasContentRelated(bonusMappings, PROVIDER_GAIA_BONUS)
  const hasPurchases = mappingsLoading ? false : hasContentRelated(bonusMappings, PROVIDER_SHOPIFY)

  const baseWrapperClass = 'user-menu__menu-wrapper'
  const wrapperClass = menuVisible
    ? `${baseWrapperClass} user-menu__menu-wrapper--visible`
    : baseWrapperClass
  const containerRef = useRef(null)
  const handleClickOutside = useCallback((event) => {
    if (containerRef.current && !containerRef.current.contains(event.target) && menuVisible) {
      toggleUserMenuVisible()
    }
  }, [menuVisible, toggleUserMenuVisible])

  useEffect(() => {
    const handleClick = (event) => {
      handleClickOutside(event)
    }

    document.addEventListener('mousedown', handleClick)

    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [handleClickOutside])

  if (auth.get('jwt')) {
    return (
      <div className="user-menu" ref={containerRef}>
        <ToolTipArrow
          direction={DIRECTION_UP}
          activeArrow="user-menu-arrow"
        />
        <Button
          type={TYPES.LINK}
          className={getClassName(hideUserMenu)}
          onClick={
            hideUserMenu
              ? null
              : _partial(
                showUserMenu,
                _partial.placeholder,
                toggleUserMenuVisible,
                setNavigationOverlayVisible,
                setNotificationsMenuVisible,
              )
          }
          aria-haspopup="true"
          aria-controls="user-menu-wrapper"
          aria-label="User menu"
        >
          {renderUserAvatar({ ...props, communityProfileEnabled, communityDisplayName })}
        </Button>
        {menuVisible ? (
          <div id="user-menu-wrapper" className={wrapperClass} onClick={onClickMenuWrapper}>
            <span className="user-menu__arrow" />
            {renderMenu({
              ...props,
              communityProfileEnabled,
              communityDisplayName,
              hasBonus,
              hasPurchases,
              trackCommunityHeaderLinkClicked,
            })}
          </div>
        ) : null}
      </div>
    )
  }
  return null
}

UserMenu.props = {
  menu: ImmutablePropTypes.map.isRequired,
  menuVisible: PropTypes.bool.isRequired,
  toggleUserMenuVisible: PropTypes.func.isRequired,
  setNavigationOverlayVisible: PropTypes.func.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  paytrack: ImmutablePropTypes.map.isRequired,
  getUserData: PropTypes.func.isRequired,
  hideUserMenu: PropTypes.bool.isRequired,
  setOverlayDialogVisible: PropTypes.func.isRequired,
}

export default connectRedux(
  (state) => ({
    currentUserPicture: selectCurrentUserProfilePicture(state),
    auth: state.auth,
    user: state.user,
    paytrack: state.paytrack,
    userAccount: state.userAccount,
    userProfiles: state.userProfiles,
    menuVisible: state.header.get('userMenuVisible'),
    menu: state.menu.getIn(['data', 'user'], EMPTY_MENU),
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      toggleUserMenuVisible: actions.header.toggleUserMenuVisible,
      setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
      setNotificationsMenuVisible: actions.header.setNotificationsMenuVisible,
      setNavigationOverlayVisible: actions.header.setNavigationOverlayVisible,
      fetchProfilePictures: actions.communityProfile.fetchProfilePictures,
    }
  },
)(UserMenu)
